import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IUser, IValuation } from '@core/models';
import { Authentication, City, Geomarker } from '@core/services';
import { ValorationModal } from '../../valoration/valoration-modal/valoration-modal.component';
import { DeleteModal } from '../../modals';
import { TranslateModule } from '@ngx-translate/core';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { CapitalizePipe, CdnUrlPipe } from '@shared/pipes';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TimeagoModule } from 'ngx-timeago';

/**
 * ### **ValorationDetailItemComponent**
 *
 * **Selector:** ``<app-valoration-detail-item>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar la sección de Valoracione y comentarios para una ciudad o geomarcador.
 */
@Component({
  selector: 'app-valoration-detail-item',
  imports: [
    TranslateModule,
    LazyLoadImageModule,
    CdnUrlPipe,
    NgbPopoverModule,
    TimeagoModule,
    CapitalizePipe,
  ],
  templateUrl: "./valoration-detail-item.component.html",
  styleUrl: "./valoration-detail-item.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValorationDetailItem {
  private readonly auth = inject(Authentication);
  private readonly matDialog = inject(MatDialog);
  private readonly cityService = inject(City);
  private readonly markerService = inject(Geomarker);
  private readonly router = inject(Router);

  /** Variable que recibe desde un componente padre la valoracion*/
  readonly valorationDetail = model.required<IValuation>();
  /** Variable que guardará la fecha de creaciond el comentario*/
  protected readonly creationDate = signal<Date | null>(null);
  /** Variable que guardará el estado de autenticación*/
  isAuthenticated: boolean;
  /** Variable que obtendra la información basica de la autentiación del usuario*/
  protected readonly user = signal<IUser | null>(this.auth.getAuthInformation());
  /** Variable que emitira un valor booleano  al componente padre*/
  readonly valuationsChange = output<boolean>();
  /** Variable que guardará el estado que indicara si es una ciudad o no*/
  private city!: boolean;

  constructor() {
    this.isAuthenticated = this.auth.isAuthenticated();
    this.auth.onAuthenticate.subscribe((value) => {
      this.isAuthenticated = value;
    });
  }

  /**
   * Función nativa d e angular que se activa una vez el constructor ha terminado sus procesos,
   * en este caso se encarga de inicializar la variable city con el valor retornado por la funcion
   * includes (true o false).
   */
  ngOnInit() {
    // includes revisa si la palabra cities se encuentra en la url
    this.city = this.router.url.includes('cities');
  }

  /**
   * Función nativa de angular que se activa cada vez que detecta un cambio sobre las variables de
   * tipo input, en este caso toma en cuenta al detalle de la valoración, si sufre un cambio actualiza
   * la fecha de creacion del mismo.
   * @param changes Parametro que detecta los cambios.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.valorationDetail) {
      const valorationDetail = this.valorationDetail();
      if (valorationDetail) {
        if (valorationDetail.creation_date) {
          this.creationDate.set(new Date(valorationDetail.creation_date));
        } else {
          this.creationDate.set(new Date());
        }
      }
    }
  }

  /**
   * Función encargada de abrir el modal que permite editar una valoración, una vez termina el proceso,
   * le llega un valor desde el mismo y con actualiza la valoración.
   */
  protected openEditValorationModal(): void {
    const dialgogRef = this.matDialog.open(ValorationModal, {
      data: {
        detail: this.valorationDetail().detail,
      },
    });

    dialgogRef.afterClosed().subscribe((value) => {
      if (value) {
        this.editComment(value.detail);
        this.valorationDetail.update((valoration) => ({
          ...valoration,
          detail: value.detail,
        }));
      }
    });
  }

  /**
   * Funcion encargada de abrir el modal que elimina una valoración, este le retorna un resultado dependiendo lo elegido
   * por el usuario, si la respuesta es correcta procede a llamar al método que elimina el comentario.
   */
  protected deleteComment(): void {
    const dialogRef = this.matDialog.open(DeleteModal, {
      data: { mensaje: 'V' },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        if (response.deleted) {
          this._deleteComment();
        }
      }
    });
  }

  /**
   * Funcion encargada de eliminar una valoración , para esto usa el id de la misma,
   * si variable city es true el identificara que la valoración pertenece a una ciudad,
   * si no, entonces sabra que es un geomarcador.
   * @private
   */
  private _deleteComment(): void {
    if (this.city) {
      this.cityService
        .deleteValuation(this.valorationDetail().id)
        .subscribe(() => this.valuationsChange.emit(true));
    } else {
      this.markerService
        .deleteValuation(this.valorationDetail().id)
        .subscribe(() => this.valuationsChange.emit(true));
    }
  }

  /**
   * Funcion encargada de editar una valoración , para esto usa el id de la misma y el contenido de la variable detail,
   * si variable city es **true** el identificara que la valoración pertenece a una ciudad,
   * si no, entonces sabra que es un geomarcador.
   * @param comment
   */
  private editComment(comment: string): void {
    if (this.city) {
      this.cityService.patchValuation(this.valorationDetail().id, { detail: comment }).subscribe();
    } else {
      this.markerService
        .patchValuation(this.valorationDetail().id, { detail: comment })
        .subscribe();
    }
  }
}
